import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
    Box,
    Group,
    Loader,
    Paper,
    Stack,
    Text,
    Flex,
    Button,
} from '@mantine/core';
import { IconPlaylistAdd, IconChevronRight } from '@tabler/icons-react';
import { useTodo } from '@/features/todo/services/useTodoQueries';
import { CATEGORY_KEY_MAP } from '@/features/todo/services/categoryService';
import { useQueryClient } from '@tanstack/react-query';
import { invalidatePlannerQueries } from '@/features/planner/services/usePlannerQueries';
import { getDateLocale } from '@/common/utils/i18nUtils';
import { i18n } from '@tickitacka/shared';
import { useNavigate } from 'react-router-dom';
import { usePlannerStore } from '@/stores/plannerStore';

interface TodoCreationResultProps {
    todoId: string;
}

/**
 * 할 일 생성 결과를 보여주는 컴포넌트
 * ToolUseBox에서 결과를 표시할 때 사용
 */
const TodoCreationResult: React.FC<TodoCreationResultProps> = ({ todoId }) => {
    const { t } = useTranslation('ai');
    // QueryClient 가져오기
    const queryClient = useQueryClient();

    // 라우팅을 위한 navigate 함수
    const navigate = useNavigate();

    // 플래너 스토어에서 날짜 설정 액션 가져오기
    const {
        dateActions: { setSelectedDate },
    } = usePlannerStore();

    // useTodo 훅을 사용해 할 일 정보 가져오기
    const { data: todo, isLoading, error } = useTodo(todoId);

    // 플래너로 이동하는 핸들러 함수
    const handlePlannerNavigation = () => {
        if (todo && todo.planners && todo.planners.length > 0) {
            try {
                // 플래너 날짜 가져오기
                const plannerDate = todo.planners[0].planner_date;
                if (plannerDate) {
                    // 플래너 페이지로 이동
                    void navigate('/planner');
                    // 해당 날짜로 플래너 설정
                    setTimeout(() => {
                        setSelectedDate(new Date(plannerDate));
                    }, 100); // 페이지 이동 후 약간의 지연을 두고 날짜 설정
                }
            } catch (error) {
                console.error('플래너 이동 중 오류 발생:', error);
            }
        }
    };

    // 투두 데이터가 로드되면 플래너 캐시 무효화
    useEffect(() => {
        if (todo) {
            // usePlannerQueries에서 가져온 invalidatePlannerQueries 함수 사용
            try {
                void invalidatePlannerQueries(queryClient, { id: todo.id });
            } catch (error) {
                // 오류 처리
                console.error('플래너 캐시 무효화 중 오류 발생:', error);
            }
        }
    }, [todo, queryClient]);

    useEffect(() => {
        console.log('TodoCreationResult mounted with id:', todoId);
        return () => {
            console.log('TodoCreationResult unmounted with id:', todoId);
        };
    }, [todoId]);

    if (isLoading) {
        return (
            <Box p="md" style={{ textAlign: 'center' }}>
                <Loader size="sm" />
                <Text size="xs" color="dimmed" mt={8}>
                    {t('todoCreation.loading')}
                </Text>
            </Box>
        );
    }

    // 에러 처리
    if (error instanceof Error || !todo) {
        return (
            <Box p="md" style={{ textAlign: 'center', color: 'red' }}>
                <Text size="sm">{t('todoCreation.loadError')}</Text>
                <Text size="xs" color="dimmed" mt={4}>
                    ID: {todoId}
                </Text>
            </Box>
        );
    }

    // 달력/일정 아이콘 표시 여부
    const hasDueDate = !!todo.due_date;
    const hasPlanner = todo.planners && todo.planners.length > 0;

    // 날짜 형식화 유틸리티 함수
    const formatDate = (dateString: string | null | undefined) => {
        if (!dateString) return '';
        const date = new Date(dateString);
        return date.toLocaleDateString(getDateLocale(), {
            year: 'numeric',
            month: 'long',
            day: 'numeric',
            weekday: 'short',
        });
    };

    return (
        <Paper
            p={0}
            radius="md"
            shadow="none"
            style={{
                width: '445px',
                overflow: 'hidden',
                background:
                    'linear-gradient(126.65deg, rgba(47, 146, 253, 0.1) -12.41%, rgba(102, 217, 232, 0.1) 84.63%), #FFFFFF',
                borderRadius: '8px',
            }}
        >
            {/* 헤더 부분 */}
            <Flex direction="column" p="md" style={{}}>
                <Flex align="center" mb={16} style={{ width: '100%' }}>
                    <Flex align="center">
                        <IconPlaylistAdd size={24} color="#228BE6" />
                        <Text
                            fw={600}
                            size="md"
                            ml={8}
                            style={{ color: '#343A40' }}
                        >
                            {t('todoCreation.creationComplete')}
                        </Text>
                    </Flex>

                    <Flex ml="auto" align="center">
                        <Text size="sm" style={{ color: '#868E96' }}>
                            {new Date(todo.register_date).toLocaleString(
                                getDateLocale(),
                                {
                                    year: 'numeric',
                                    month: '2-digit',
                                    day: '2-digit',
                                    hour: '2-digit',
                                    minute: '2-digit',
                                    hour12: false,
                                }
                            )}
                            {t('todoCreation.createdAt')}
                        </Text>
                    </Flex>
                </Flex>

                {/* 내용 부분 */}
                <Box
                    p="lg"
                    style={{
                        backgroundColor: '#FFFFFF',
                        borderRadius: '16px',
                        marginTop: '-6px',
                        width: '100%',
                        boxSizing: 'border-box',
                    }}
                >
                    {/* 제목 */}
                    <Text fw={700} size="lg" style={{ color: '#333333' }}>
                        {todo.title}
                    </Text>

                    {/* 내용 (있는 경우만) */}
                    {todo.content && (
                        <Text size="sm" style={{ color: '#495057' }}>
                            {todo.content}
                        </Text>
                    )}

                    {/* 일정 및 카테고리 정보 */}
                    <Stack gap={8} style={{ marginTop: '16px' }}>
                        {hasPlanner && todo.planners?.[0] && (
                            <Flex align="center">
                                <Text
                                    size="sm"
                                    fw={600}
                                    style={{ color: '#333333' }}
                                >
                                    {t('todoCreation.schedule')}
                                </Text>
                                <Text
                                    size="sm"
                                    style={{
                                        color: '#555555',
                                        marginLeft: '8px',
                                    }}
                                >
                                    {formatDate(todo.planners[0].planner_date)}
                                </Text>
                            </Flex>
                        )}

                        {/* 카테고리 정보 */}
                        {todo.category && (
                            <Flex align="center" justify="space-between">
                                <Flex align="center">
                                    <Text
                                        size="sm"
                                        fw={600}
                                        style={{ color: '#333333' }}
                                    >
                                        {t('todoCreation.category')}
                                    </Text>
                                    <Flex
                                        align="center"
                                        gap={6}
                                        style={{ marginLeft: '8px' }}
                                    >
                                        <Box
                                            style={{
                                                width: '12px',
                                                height: '12px',
                                                borderRadius: '100px',
                                                backgroundColor:
                                                    (todo.category &&
                                                        typeof todo.category ===
                                                            'object' &&
                                                        'color' in
                                                            todo.category &&
                                                        todo.category.color) ||
                                                    '#2F92FD',
                                            }}
                                        />
                                        <Text
                                            size="sm"
                                            style={{ color: '#555555' }}
                                        >
                                            {(() => {
                                                // 안전하게 카테고리 이름 처리
                                                if (
                                                    todo.category &&
                                                    typeof todo.category ===
                                                        'object' &&
                                                    'name' in todo.category &&
                                                    todo.category.name
                                                ) {
                                                    const categoryName = String(
                                                        todo.category.name
                                                    );
                                                    const categoryKey =
                                                        CATEGORY_KEY_MAP[
                                                            categoryName
                                                        ];
                                                    if (categoryKey) {
                                                        return i18n.t(
                                                            categoryKey
                                                        );
                                                    }
                                                    return categoryName;
                                                }
                                                return t('todoCreation.defaultCategory');
                                            })()}
                                        </Text>
                                    </Flex>
                                </Flex>

                                {/* 플래너로 이동 버튼 - 카테고리와 같은 행에 배치 */}
                                {hasPlanner && (
                                    <Button
                                        variant="subtle"
                                        size="xs"
                                        rightSection={
                                            <IconChevronRight
                                                size={16}
                                                color="#228BE6"
                                                style={{ marginLeft: '-6px' }} // 아이콘과 텍스트 사이 간격 줄이기
                                            />
                                        }
                                        onClick={handlePlannerNavigation}
                                        px={0} // 좌우 패딩 줄이기
                                    >
                                        {t('todoCreation.goToPlanner')}
                                    </Button>
                                )}
                            </Flex>
                        )}
                    </Stack>
                </Box>
            </Flex>
        </Paper>
    );
};

export default TodoCreationResult;
